import {Component, computed, effect, ElementRef, inject, OnDestroy, input, viewChild} from '@angular/core';
import {TaListBase} from '../../../pages/ta-list-base.directive';
import {FormsModule} from '@angular/forms';
import {TableModule} from 'primeng/table';
import {Button} from 'primeng/button';
import {DatePipe, NgClass} from '@angular/common';
import {Chip} from 'primeng/chip';
import {InputText} from 'primeng/inputtext';
import {ManagerTaListSource, toTAStatusText} from '../../../../util/app.types';
import {LocationService} from '../../../../services/location.service';
import {ScrollHeightDirective} from '../../../directives/scroll-height.directive';
import {PrepaidIndicatorComponent} from '../../prepaid-indicator/prepaid-indicator.component';
import {AttachmentIndicatorComponent} from '../../attachment-indicator/attachment-indicator.component';
import {TaCostBubbleComponent} from '../../ta-cost-bubble/ta-cost-bubble.component';
import {TaCommentCount} from '../../ta-comments/ta-comment-count';

/**
 * The paged, view-only manager dashboard tabs: My Direct Report TA History, My Unit TAs and My Unit
 * TAs History. All three are the same table over a paged store cache, so one component covers them
 * and the source input picks the cache and loader.
 */
@Component({
  selector: 'manager-ta-list',
  imports: [
    TaCommentCount,
    Button,
    Chip,
    DatePipe,
    FormsModule,
    InputText,
    NgClass,
    TableModule,
    ScrollHeightDirective,
    PrepaidIndicatorComponent,
    AttachmentIndicatorComponent,
    TaCostBubbleComponent,
  ],
  templateUrl: './manager-ta-list.component.html',
})
export class ManagerTaListComponent extends TaListBase implements OnDestroy {
  source = input.required<ManagerTaListSource>();

  private scrollContainer = viewChild<ElementRef>('scrollContainer');
  private loadMoreSentinel = viewChild<ElementRef>('loadMoreSentinel');
  private observer: IntersectionObserver | undefined;
  private loadingDebounceTimer: ReturnType<typeof setTimeout> | undefined;

  protected locationService = inject(LocationService);

  cache = computed(() => {
    switch (this.source()) {
      case 'directReportHistory': return this.store.directReportHistoryTaListCache();
      case 'unitActive': return this.store.unitActiveTaListCache();
      case 'unitClosed': return this.store.unitClosedTaListCache();
    }
  });

  // Drives the testids as well as the copy, so each tab stays independently addressable.
  testIdPrefix = computed(() => {
    switch (this.source()) {
      case 'directReportHistory': return 'manager-direct-report-history';
      case 'unitActive': return 'manager-unit-active';
      case 'unitClosed': return 'manager-unit-closed';
    }
  });

  emptyMessage = computed(() => {
    switch (this.source()) {
      case 'directReportHistory': return 'Your direct reports have no finished TAs.';
      case 'unitActive': return 'There are no in-flight TAs for your units.';
      case 'unitClosed': return 'There are no finished TAs for your units.';
    }
  });

  isLoading = computed(() => this.store.isLoadingData());
  canLoadMore = computed(() => {
    const cache = this.cache();
    if (!cache?.totalCount) return false;
    return (cache.currentCount ?? 0) < cache.totalCount;
  });

  constructor() {
    super();

    effect(() => {
      this.availableTaList.set(this.cache()?.taList ?? []);
    });

    effect(() => {
      const el = this.loadMoreSentinel()?.nativeElement;
      const root = this.scrollContainer()?.nativeElement;
      this.observer?.disconnect();
      if (el && root) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting) {
              this.loadMore();
            }
          },
          { root, threshold: 0.1 },
        );
        this.observer.observe(el);
      }
    });

    effect(() => {
      const loading = this.isLoading();
      if (loading) {
        this.loadingDebounceTimer = setTimeout(() => {
          this.loadingService.showLoading({message: 'Loading...'});
        }, 80);
      } else {
        clearTimeout(this.loadingDebounceTimer);
        this.loadingDebounceTimer = undefined;
        this.loadingService.hideLoading();
      }
    });
  }

  // The loader decides whether to refetch - it reuses a cache newer than LIST_CACHE_TTL_MS.
  override ngOnInit(): void {
    super.ngOnInit();
    this.load();
    this.locationService.loadLocations();
  }

  // The explicit refresh button, which must always hit the server.
  reloadTaList(): void {
    switch (this.source()) {
      case 'directReportHistory':
        this.store.resetDirectReportHistoryCache();
        break;
      case 'unitActive':
        this.store.resetUnitCache('active');
        break;
      case 'unitClosed':
        this.store.resetUnitCache('closed');
        break;
    }
    this.load();
  }

  loadMore(): void {
    if (!this.canLoadMore() || this.isLoading()) return;
    switch (this.source()) {
      case 'directReportHistory':
        this.taSearchDatasource.loadMoreDirectReportHistoryTas();
        break;
      case 'unitActive':
        this.taSearchDatasource.loadMoreUnitTas('active');
        break;
      case 'unitClosed':
        this.taSearchDatasource.loadMoreUnitTas('closed');
        break;
    }
  }

  private load(): void {
    switch (this.source()) {
      case 'directReportHistory':
        this.taSearchDatasource.loadDirectReportHistoryTaList();
        break;
      case 'unitActive':
        this.taSearchDatasource.loadUnitTaList('active');
        break;
      case 'unitClosed':
        this.taSearchDatasource.loadUnitTaList('closed');
        break;
    }
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    clearTimeout(this.loadingDebounceTimer);
    this.observer?.disconnect();
  }

  protected readonly toTAStatusText = toTAStatusText;
}
